import {useState, useEffect, useCallback, useRef, useContext} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Tabs, Empty, Input, notification, Spin} from 'antd';
import {FullscreenOutlined, FullscreenExitOutlined, BarcodeOutlined, SearchOutlined, UserOutlined, PrinterOutlined} from '@ant-design/icons';
import {posActions, posTabsSelector, posActiveTabSelector, posActiveTabDataSelector} from '~/reduxs/Pos/posSlice';
import {currentUserSelector} from '~/reduxs/Auth/authSlice';
import {Button, Loading, Icon, Image} from '~/components';
import ProductItem from '../components/ProductItem';
import CartItem from '../components/CartItem';
import SuggestMoney from '../components/SuggestMoney';
import BillPrint from '../components/BillPrint';
import SyncStatus from '../components/SyncStatus';
import AddCustomerModal from '../components/AddCustomerModal';
import {formatCurrency, numberFormat, handleRequest, apiError} from '~/utils';
import {orderApi, utilsApi, customerApi} from '~/api';
import {useReactToPrint} from 'react-to-print';
import {useLocalProducts} from '~/hooks';
import indexedDBService from '~/services/indexedDBService';
import {AppContext} from "../../../context/AppProvider";
import '../style/Pos.scss';

const {TabPane} = Tabs;

const Pos = () =>
{
    const {appData} = useContext(AppContext);

    const dispatch = useDispatch();

    const tabs = useSelector(posTabsSelector);
    const activeTabId = useSelector(posActiveTabSelector);
    const activeTab = useSelector(posActiveTabDataSelector);

    const currentUser = useSelector(currentUserSelector);

    // Local products hook
    const { searchProducts, isReady: localProductsReady } = useLocalProducts();

    const [customerSearch, setCustomerSearch] = useState('');
    const [customers, setCustomers] = useState([]);
    const [customersLoading, setCustomersLoading] = useState(false);
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const [productSearch, setProductSearch] = useState('');
    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [discount, setDiscount] = useState(0);
    const [discountType, setDiscountType] = useState('amount');
    const [paid, setPaid] = useState(0);
    const [paidDisplay, setPaidDisplay] = useState('0'); // Formatted display value
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [barcodeMode, setBarcodeMode] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [showProducts, setShowProducts] = useState(false);
    const [mobileCartTab, setMobileCartTab] = useState('items'); // 'items' or 'checkout'
    const [qrLoading, setQrLoading] = useState(false);
    const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
    const [lastCreatedOrder, setLastCreatedOrder] = useState(null);

    const searchTimeoutRef = useRef(null);
    const customerSearchTimeoutRef = useRef(null);
    const billPrintRef = useRef(null);
    const orderDataRef = useRef(null); // Ref to store order data for printing

    // Load products from IndexedDB
    const loadProducts = useCallback(async (keyword = '') =>
    {
        if (!localProductsReady)
        {
            return;
        }

        setProductsLoading(true);

        try
        {
            let results;

            if (keyword && keyword.trim() !== '') {
                results = await searchProducts(keyword, 100);
            }
            else
            {
                // Load all products or top 100
                results = await searchProducts('', 100);
            }

            setProducts(results);
        }
        catch (error)
        {
            notification.error({
                message: 'Lỗi',
                description: 'Không thể tải danh sách sản phẩm'
            });
        }
        finally
        {
            setProductsLoading(false);
        }
    }, [searchProducts, localProductsReady]);

    // Load products on mount
    useEffect(() => {
        if (localProductsReady) {
            loadProducts();
        }
    }, [localProductsReady, loadProducts]);

    // Load offline invoice on mount
    useEffect(() => {
        const loadOfflineInvoice = async () => {
            try {
                const offlineData = await indexedDBService.getOfflineInvoice();

                if (offlineData && offlineData.tabs && offlineData.tabs.length > 0) {
                    // Check if offline data has actual content
                    const hasContent = offlineData.tabs.some(tab =>
                        tab.items.length > 0 ||
                        tab.customer !== null ||
                        tab.discount > 0 ||
                        tab.paid > 0
                    );

                    if (hasContent) {
                        console.log('📋 Restoring offline invoice...', offlineData);
                        dispatch(posActions.restoreState({
                            tabs: offlineData.tabs,
                            activeTab: offlineData.activeTab,
                            nextTabId: offlineData.nextTabId,
                        }));

                        notification.success({
                            message: 'Khôi phục hóa đơn',
                            description: 'Đã khôi phục hóa đơn chưa hoàn thành',
                            duration: 3,
                        });
                    }
                }
            } catch (error) {
                console.error('Failed to load offline invoice:', error);
            }
        };

        loadOfflineInvoice();
    }, [dispatch]);

    // Search customers function
    const searchCustomers = useCallback(async (keyword) => {
        if (!keyword || keyword.trim() === '') {
            setCustomers([]);
            setShowCustomerDropdown(false);
            return;
        }

        setCustomersLoading(true);

        try {
            let [error, response] = await handleRequest(
                customerApi.gets({ keyword, limit: 10 })
            );

            if (!apiError('Tìm kiếm khách hàng thất bại', error, response)) {
                setCustomers(response.data || []);
                setShowCustomerDropdown(true);
            }
        } catch (err) {
            console.error('Error searching customers:', err);
        } finally {
            setCustomersLoading(false);
        }
    }, []);

    // Debounced customer search effect
    useEffect(() => {
        if (customerSearchTimeoutRef.current) {
            clearTimeout(customerSearchTimeoutRef.current);
        }

        customerSearchTimeoutRef.current = setTimeout(() => {
            searchCustomers(customerSearch);
        }, 300);

        return () => {
            if (customerSearchTimeoutRef.current) {
                clearTimeout(customerSearchTimeoutRef.current);
            }
        };
    }, [customerSearch, searchCustomers]);

    // Debounced search effect
    useEffect(() => {
        // Clear timeout nếu có
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Set timeout mới để debounce
        searchTimeoutRef.current = setTimeout(() => {
            loadProducts(productSearch);
        }, 300);

        // Cleanup function
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [productSearch, loadProducts]);

    // Sync discount and paid with active tab
    useEffect(() => {
        if (activeTab) {
            setDiscount(activeTab.discount);
            setDiscountType(activeTab.discountType);
            setPaid(activeTab.paid);
            setPaidDisplay(numberFormat(activeTab.paid, 0));
        }
    }, [activeTab]);

    // Auto-update paid amount when total changes (only if not manually set)
    useEffect(() => {
        if (activeTab && !activeTab.isPaidManuallySet && activeTab.items.length > 0) {
            // Calculate total inline to avoid dependency issues
            const subtotal = activeTab.items.reduce((sum, item) => {
                if (item.unit === 'kg') {
                    return sum + item.price * item.quantity * 10;
                }
                return sum + item.price * item.quantity;
            }, 0);

            const totalVat = activeTab.items.reduce((sum, item) => {
                const itemSubtotal = item.unit === 'kg'
                    ? item.price * item.quantity * 10
                    : item.price * item.quantity;
                return sum + (itemSubtotal * (item.vat || 0) / 100);
            }, 0);

            const totalBeforeDiscount = subtotal + totalVat;

            const currentDiscount = activeTab.discount || 0;
            const currentDiscountType = activeTab.discountType || 'amount';

            const discountAmount = currentDiscountType === 'percent'
                ? (totalBeforeDiscount * currentDiscount / 100)
                : currentDiscount;

            const total = totalBeforeDiscount - discountAmount;

            // Only update if different
            if (Math.abs(total - activeTab.paid) > 0.01) {
                setPaid(total);
                setPaidDisplay(numberFormat(total, 0));
                dispatch(posActions.setPaid({
                    tabId: activeTabId,
                    paid: total,
                    isManual: false // Auto-update, not manual
                }));
            }
        }
    }, [activeTab?.items, activeTab?.discount, activeTab?.discountType, activeTab?.isPaidManuallySet, activeTab?.paid, activeTabId, dispatch]);

    const handleAddTab = () => {
        dispatch(posActions.addTab());
    };

    const handleRemoveTab = (tabId) => {
        if (tabs.length > 1) {
            dispatch(posActions.removeTab(tabId));
        }
    };

    const handleTabChange = (tabId) => {
        dispatch(posActions.setActiveTab(parseInt(tabId)));
        setLastCreatedOrder(null); // Clear last order when switching tabs
        orderDataRef.current = null;
    };

    const handleAddProduct = (product) => {
        // Clear last order data when starting a new order
        if (activeTab?.items?.length === 0 && lastCreatedOrder) {
            setLastCreatedOrder(null);
            orderDataRef.current = null;
        }

        // Set initial quantity based on unit
        const initialQuantity = product.unit === 'kg' ? 0.1 : 1;

        dispatch(posActions.addItem({
            tabId: activeTabId,
            item: {
                ...product,
                quantity: initialQuantity
            }
        }));

        // Hide products panel on mobile after adding product
        setShowProducts(false);
    };

    const handleIncreaseQuantity = (itemId) => {
        const item = activeTab.items.find(i => i.id === itemId);
        if (item) {
            const step = item.unit === 'kg' ? 0.1 : 1;
            dispatch(posActions.updateItemQuantity({
                tabId: activeTabId,
                itemId,
                quantity: item.quantity + step
            }));
        }
    };

    const handleDecreaseQuantity = (itemId) => {
        const item = activeTab.items.find(i => i.id === itemId);
        if (item && item.quantity > (item.unit === 'kg' ? 0.1 : 1)) {
            const step = item.unit === 'kg' ? 0.1 : 1;
            dispatch(posActions.updateItemQuantity({
                tabId: activeTabId,
                itemId,
                quantity: item.quantity - step
            }));
        }
    };

    const handleQuantityChange = (itemId, quantity) => {
        if (quantity >= 0) {
            dispatch(posActions.updateItemQuantity({
                tabId: activeTabId,
                itemId,
                quantity
            }));
        }
    };

    const handleRemoveItem = (itemId) => {
        dispatch(posActions.removeItem({
            tabId: activeTabId,
            itemId
        }));
    };

    const handleDiscountChange = (value) => {
        setDiscount(value);
        dispatch(posActions.setDiscount({
            tabId: activeTabId,
            discount: value,
            discountType
        }));
    };

    const handleDiscountTypeChange = (type) => {
        setDiscountType(type);
        dispatch(posActions.setDiscount({
            tabId: activeTabId,
            discount,
            discountType: type
        }));
    };

    const handlePaidChange = (value) => {
        // Remove all non-numeric characters except decimal point
        const numericValue = parseFloat(value.toString().replace(/[^\d.]/g, '')) || 0;
        setPaid(numericValue);
        setPaidDisplay(numberFormat(numericValue, 0));
        dispatch(posActions.setPaid({
            tabId: activeTabId,
            paid: numericValue,
            isManual: true // User manually changed the value
        }));
    };

    const handlePaymentMethodChange = async (method) => {
        dispatch(posActions.setPaymentMethod({
            tabId: activeTabId,
            paymentMethod: method
        }));

        if (method === 'transfer') {
            // Load QR code when switching to transfer
            await loadQrCode();
        } else {
            // Clear QR code when switching to cash
            dispatch(posActions.setQrCodeUrl({
                tabId: activeTabId,
                qrCodeUrl: ''
            }));
        }
    };

    const handleOpenAddCustomer = () => {
        setShowAddCustomerModal(true);
    };

    const handleCloseAddCustomer = () => {
        setShowAddCustomerModal(false);
    };

    const handleAddCustomerSuccess = (customer) => {
        // Add customer to active tab
        dispatch(posActions.setCustomer({
            tabId: activeTabId,
            customer: {
                id: customer.id,
                code: customer.code,
                name: customer.name,
                phone: customer.phone,
                address: customer.address,
            }
        }));

        notification.success({
            message: 'Thành công',
            description: `Đã thêm khách hàng: ${customer.name}`
        });
    };

    const handleClearCustomer = () => {
        dispatch(posActions.setCustomer({
            tabId: activeTabId,
            customer: null
        }));
        setCustomerSearch('');
    };

    const handleSelectCustomer = (customer) => {
        dispatch(posActions.setCustomer({
            tabId: activeTabId,
            customer: {
                id: customer.id,
                code: customer.code,
                name: customer.name,
                phone: customer.phone,
                address: customer.address,
            }
        }));
        setCustomerSearch('');
        setShowCustomerDropdown(false);
        setCustomers([]);
    };

    const loadQrCode = async () => {
        if (!activeTab || activeTab.items.length === 0) {
            return;
        }

        const totals = calculateTotals();

        setQrLoading(true);
        let [error, response] = await handleRequest(
            utilsApi.qrPayment({
                amount: totals.total,
                description: `Thanh toán đơn hàng`
            })
        );
        setQrLoading(false);

        if (!apiError('Tạo mã QR thất bại', error, response)) {
            const qrUrl = response.data?.qr_url || '';
            // Dispatch vào Redux
            dispatch(posActions.setQrCodeUrl({
                tabId: activeTabId,
                qrCodeUrl: qrUrl
            }));
        }
    };

    const handleCheckout = async () => {
        // Validate
        if (!activeTab || activeTab.items.length === 0)
        {
            notification.error({
                message: 'Lỗi',
                description: 'Vui lòng thêm sản phẩm vào giỏ hàng'
            });
            return;
        }

        if (paid < totals.total) {
            notification.error({
                message: 'Lỗi',
                description: 'Số tiền khách đưa không đủ'
            });
            return;
        }

        setCheckoutLoading(true);

        // Prepare order data
        const orderData = {
            customer_id: activeTab.customer?.id || null,
            customer_name: activeTab.customer?.name || '',
            customer_phone: activeTab.customer?.phone || '',
            items: activeTab.items.map(item => ({
                id: item.id,
                title: item.title,
                code: item.code,
                price: item.price,
                quantity: item.quantity,
                unit: item.unit,
                vat: item.vat || 0,
                attribute_label: item.attribute_label || '',
            })),
            subtotal: totals.subtotal,
            total_vat: totals.totalVat,
            discount: discount,
            discount_type: discountType,
            discount_amount: totals.discountAmount,
            total: totals.total,
            paid: paid,
            change: totals.change,
            payment_method: activeTab?.paymentMethod || 'cash',
            note: '',
        };

        let [error, response] = await handleRequest(orderApi.add(orderData));

        setCheckoutLoading(false);

        if (!apiError('Tạo đơn hàng thất bại', error, response)) {
            // Lưu thông tin order từ server vào ref (đồng bộ)
            const orderData = response.data;
            orderDataRef.current = orderData;
            setLastCreatedOrder(orderData); // Cũng lưu vào state để có thể in lại sau

            notification.success({
                message: 'Thành công',
                description: 'Thanh toán thành công!'
            });

            // Delete offline invoice after successful checkout
            indexedDBService.deleteOfflineInvoice().catch(err => {
                console.error('Failed to delete offline invoice:', err);
            });

            // Clear cart BEFORE printing
            handleClearCart();

            // Auto print bill after checkout with server data
            // Sử dụng setTimeout để đảm bảo DOM đã được cập nhật
            setTimeout(() => {
                console.log('=== PRINTING WITH DATA ===');
                console.log('orderDataRef.current:', orderDataRef.current);
                handlePrint();
            }, 200);
        }
    };

    const handlePrint = useReactToPrint({
        contentRef: billPrintRef,
        documentTitle: `Bill_${activeTab?.code || 'HD001'}`,
        pageStyle: `
            @page {
                size: 58mm auto;
                margin: 0;
            }
            @media print {
                body {
                    margin: 0;
                    padding: 0;
                }
            }
        `
    });

    const handleClearCart = () => {
        dispatch(posActions.clearTab(activeTabId));
        setDiscount(0);
        setPaid(0);
        // Không xóa lastCreatedOrder ở đây để vẫn có thể in lại hóa đơn
    };

    const toggleFullscreen = () =>
    {
        if (!document.fullscreenElement)
        {
            document.documentElement.requestFullscreen();
            setIsFullscreen(true);
        }
        else
        {
            if (document.exitFullscreen)
            {
                document.exitFullscreen();
                setIsFullscreen(false);
            }
        }
    };

    const handleProductSearch = (value) =>
    {
        setProductSearch(value);
    };

    const handleSearchFocus = () => {
        // Show products panel when search is focused on mobile
        setShowProducts(true);
    };

    const handleProductsClose = () => {
        // Hide products panel on mobile
        setShowProducts(false);
    };

    const handleBarcodeToggle = () =>
    {
        setBarcodeMode(!barcodeMode);
        // Auto focus vào search input khi bật barcode mode
        if (!barcodeMode)
        {
            setTimeout(() => {
                document.getElementById('pos-search-input')?.focus();
            }, 100);
        }
    };

    // Helper function: Calculate item subtotal based on unit
    const calculateItemSubtotal = (item) => {
        if (item.unit === 'kg') {
            // Giá niêm yết là giá/100g, quantity là kg
            // subtotal = quantity * 10 * price
            return item.price * item.quantity * 10;
        }
        // Các unit khác: subtotal = quantity * price
        return item.price * item.quantity;
    };

    // Calculate totals
    const calculateTotals = () =>
    {
        if (!activeTab || !activeTab.items.length)
        {
            return {
                subtotal: 0,
                totalVat: 0,
                discountAmount: 0,
                total: 0,
                change: 0
            };
        }

        const subtotal = activeTab.items.reduce((sum, item) => {
            return sum + calculateItemSubtotal(item);
        }, 0);

        const totalVat = activeTab.items.reduce((sum, item) => {
            const itemSubtotal = calculateItemSubtotal(item);
            return sum + (itemSubtotal * (item.vat || 0) / 100);
        }, 0);

        const totalBeforeDiscount = subtotal + totalVat;

        const discountAmount = discountType === 'percent'
            ? (totalBeforeDiscount * discount / 100)
            : discount;

        const total = totalBeforeDiscount - discountAmount;
        const change = paid - total;

        return {
            subtotal,
            totalVat,
            discountAmount,
            total,
            change: change > 0 ? change : 0
        };
    };

    const totals = calculateTotals();

    return (
        <div className="pos-container">
            {/* Header Bar */}
            <div className="pos-header">
                <div className="pos-header-search">
                    <Button background white to={'/'}>Quan lại</Button>
                    <div className="pos-search">
                        <Input
                            id="pos-search-input"
                            className="pos-search-input"
                            placeholder={localProductsReady ? "Tìm kiếm theo tên sản phẩm, mã SKU hoặc mã vạch..." : "Đang tải dữ liệu sản phẩm..."}
                            value={productSearch}
                            onChange={(e) => handleProductSearch(e.target.value)}
                            onFocus={handleSearchFocus}
                            size="large"
                            autoFocus={barcodeMode}
                            disabled={!localProductsReady}
                        />
                        {!localProductsReady && (
                            <div className="pos-search-loading">
                                <Spin /> Đang tải dữ liệu...
                            </div>
                        )}
                    </div>
                    <button className={`barcode-btn ${barcodeMode ? 'active' : ''}`} onClick={handleBarcodeToggle} title="Quét mã vạch">
                        <BarcodeOutlined />
                        {barcodeMode && <span className="barcode-indicator">Scanning...</span>}
                    </button>
                </div>
                <div className="pos-tabs-wrapper">
                    <Tabs
                        type="editable-card"
                        activeKey={activeTabId.toString()}
                        onChange={handleTabChange}
                        addIcon={Icon.plusCircle}
                        onEdit={(targetKey, action) => {
                            if (action === 'add') {
                                handleAddTab();
                            } else {
                                handleRemoveTab(parseInt(targetKey));
                            }
                        }}
                        className="pos-tabs"
                    >
                        {tabs.map(tab => (
                            <TabPane
                                tab={tab.name}
                                key={tab.id.toString()}
                                closable={tabs.length > 1}
                            />
                        ))}
                    </Tabs>
                </div>
                <div className="pos-header-actions d-flex">
                    <SyncStatus />
                    <button className="fullscreen-btn" onClick={toggleFullscreen} title="Toàn màn hình">
                        {isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                    </button>
                    {currentUser && (
                        <div className="pos-account">
                            <div className="pos-account-avatar">
                                {currentUser.avatar ? (
                                    <Image src={currentUser.avatar} alt={currentUser.firstname} />
                                ) : (
                                    <UserOutlined />
                                )}
                            </div>
                            <div className="pos-account-info">
                                <span className="pos-account-name">
                                    {currentUser.lastname}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Main Content */}
            <div className="pos-content">
                {/* Products Section */}
                <div className={`pos-products-section ${showProducts ? 'show-mobile' : ''}`}>
                    {/* Mobile Backdrop */}
                    <div className="pos-products-backdrop" onClick={handleProductsClose}></div>
                    <div className="pos-products-grid">
                        {productsLoading && <Loading noFixed />}
                        {!productsLoading && products.length === 0 && (
                            <div className="empty-wrapper">
                                <Empty description="Không có sản phẩm nào" />
                            </div>
                        )}
                        {!productsLoading && products.map(product => (
                            <ProductItem
                                key={product.id}
                                product={product}
                                appData={appData}
                                onAdd={handleAddProduct}
                            />
                        ))}
                    </div>
                </div>

                {/* Cart Section */}
                <div className="pos-cart-section">
                    {/* Mobile Tabs */}
                    <div className="pos-mobile-tabs">
                        <button
                            className={`pos-mobile-tab ${mobileCartTab === 'items' ? 'active' : ''}`}
                            onClick={() => setMobileCartTab('items')}
                        >Giỏ hàng ({activeTab?.items?.length || 0})</button>
                        <button
                            className={`pos-mobile-tab ${mobileCartTab === 'checkout' ? 'active' : ''}`}
                            onClick={() => setMobileCartTab('checkout')}
                        >Thanh toán</button>
                    </div>

                    <div className="pos-cart-content">
                        {/* Left Column - Cart Items */}
                        <div className={`pos-cart-items-column ${mobileCartTab === 'items' ? 'show-mobile' : ''}`}>
                            <div className="pos-cart-items">
                                {(!activeTab || activeTab.items.length === 0) && (
                                    <Empty description="Chưa có sản phẩm nào" />
                                )}
                                {activeTab && activeTab.items.map(item => (
                                    <CartItem
                                        key={item.id}
                                        item={item}
                                        onIncrease={handleIncreaseQuantity}
                                        onDecrease={handleDecreaseQuantity}
                                        onRemove={handleRemoveItem}
                                        onQuantityChange={handleQuantityChange}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Right Column - Customer Info & Summary */}
                        <div className={`pos-checkout-sidebar ${mobileCartTab === 'checkout' ? 'show-mobile' : ''}`}>
                            {/* Customer Info */}
                            <div className="pos-checkout-section">
                                {!activeTab?.customer && (
                                    <div className="pos-customer-search-wrapper">
                                        <div className="pos-customer-search">
                                            <Input
                                                placeholder="Tìm khách hàng (SĐT/Tên)"
                                                value={customerSearch}
                                                onChange={(e) => setCustomerSearch(e.target.value)}
                                                prefix={<SearchOutlined />}
                                                className="customer-search-input"
                                            />
                                            <Button
                                                background
                                                green
                                                className="add-customer-btn"
                                                onClick={handleOpenAddCustomer}
                                                title="Thêm khách hàng mới"
                                            >
                                                {Icon.plus}
                                            </Button>
                                        </div>

                                        {/* Customer search dropdown */}
                                        {showCustomerDropdown && (
                                            <div className="customer-search-dropdown">
                                                {customersLoading && (
                                                    <div className="customer-search-loading">
                                                        <Spin /> Đang tìm...
                                                    </div>
                                                )}

                                                {!customersLoading && customers.length === 0 && (
                                                    <div className="customer-search-empty">
                                                        Không tìm thấy khách hàng
                                                    </div>
                                                )}

                                                {!customersLoading && customers.length > 0 && (
                                                    <div className="customer-search-results">
                                                        {customers.map(customer => (
                                                            <div
                                                                key={customer.id}
                                                                className="customer-search-item"
                                                                onClick={() => handleSelectCustomer(customer)}
                                                            >
                                                                <div className="customer-item-icon">
                                                                    <UserOutlined />
                                                                </div>
                                                                <div className="customer-item-info">
                                                                    <div className="customer-item-name">{customer.name}</div>
                                                                    <div className="customer-item-phone">{customer.phone}</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab?.customer && (
                                    <div className="pos-customer-info">
                                        <div className="pos-customer-info-data">
                                            <div className="avatar">
                                                {Icon.user}
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold mb-1">{activeTab.customer.name}</p>
                                                <p className="text-xs text-primary font-medium flex items-center gap-1">
                                                    {activeTab.customer.phone}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="pos-customer-info-action">
                                            <Button
                                                className="clear-customer-btn"
                                                onClick={handleClearCustomer}
                                                title="Xóa khách hàng"
                                            >
                                                {Icon.close}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Payment Summary */}
                            <div className="pos-summary">
                                <div className="summary-row">
                                    <span>Tổng tiền hàng:</span>
                                    <strong className="money">{formatCurrency(totals.subtotal)}</strong>
                                </div>
                                <div className="summary-row">
                                    <span>Tổng VAT:</span>
                                    <strong className="money">{formatCurrency(totals.totalVat)}</strong>
                                </div>
                                <div className="summary-row discount-row">
                                    <span>Giảm giá:</span>
                                    <div className="discount-controls">
                                        <input
                                            type="number"
                                            value={discount}
                                            onChange={(e) => handleDiscountChange(parseFloat(e.target.value) || 0)}
                                            className="discount-input"
                                        />
                                        <select
                                            value={discountType}
                                            onChange={(e) => handleDiscountTypeChange(e.target.value)}
                                            className="discount-type"
                                        >
                                            <option value="amount">VNĐ</option>
                                            <option value="percent">%</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="summary-row total-row">
                                    <strong>Khách cần trả:</strong>
                                    <strong className="total-amount money">{formatCurrency(totals.total)}</strong>
                                </div>
                                <div className="summary-row paid-row">
                                    <strong>Khách đã trả:</strong>
                                    <input
                                        type="text"
                                        value={paidDisplay}
                                        onChange={(e) => handlePaidChange(e.target.value)}
                                        onFocus={(e) => e.target.select()}
                                        className="paid-input"
                                        placeholder="0"
                                    />
                                </div>

                                {/* Payment Method Selection */}
                                <div className="summary-row payment-method-row">
                                    <span>Hình thức thanh toán:</span>
                                    <div className="payment-method-controls">
                                        <label className="payment-method-option">
                                            <input
                                                type="radio"
                                                name="paymentMethod"
                                                value="cash"
                                                checked={activeTab?.paymentMethod === 'cash'}
                                                onChange={(e) => handlePaymentMethodChange(e.target.value)}
                                            />
                                            <span>Tiền mặt</span>
                                        </label>
                                        <label className="payment-method-option">
                                            <input
                                                type="radio"
                                                name="paymentMethod"
                                                value="transfer"
                                                checked={activeTab?.paymentMethod === 'transfer'}
                                                onChange={(e) => handlePaymentMethodChange(e.target.value)}
                                            />
                                            <span>Chuyển khoản</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Suggest Money - Only show for cash payment */}
                                {activeTab?.paymentMethod === 'cash' && (
                                    <SuggestMoney
                                        requiredAmount={totals.total}
                                        onSelect={handlePaidChange}
                                    />
                                )}

                                {/* QR Code - Only show for transfer payment */}
                                {activeTab?.paymentMethod === 'transfer' && (
                                    <div className="qr-code-section">
                                        {qrLoading && <Loading noFixed />}
                                        {!qrLoading && activeTab?.qrCodeUrl && (
                                            <div className="qr-code-wrapper">
                                                <img src={activeTab.qrCodeUrl} alt="QR Code" className="qr-code-image" />
                                                <p className="qr-code-hint">Quét mã để thanh toán</p>
                                            </div>
                                        )}
                                        {!qrLoading && !activeTab?.qrCodeUrl && (
                                            <div className="qr-code-error">
                                                <p>Không thể tải mã QR. Vui lòng thử lại.</p>
                                                <Button small background blue onClick={loadQrCode}>Tải lại</Button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="summary-row change-row">
                                    <span>Tiền thừa trả khách:</span>
                                    <strong className="change-amount money">{formatCurrency(totals.change)}</strong>
                                </div>
                            </div>

                            <div className="pos-actions">
                                <Button outline red onClick={handleClearCart} leftIcon={Icon.delete}>
                                    Xóa
                                </Button>
                                <Button
                                    background
                                    primary
                                    onClick={handleCheckout}
                                    leftIcon={Icon.success}
                                    disabled={!activeTab || activeTab.items.length === 0 || checkoutLoading}
                                >
                                    {checkoutLoading ? 'Đang xử lý...' : 'Thanh toán'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bill Print Component - Hidden */}
            <div style={{display: 'none'}}>
                <BillPrint
                    ref={billPrintRef}
                    billData={{
                        code: orderDataRef.current?.code || lastCreatedOrder?.code || activeTab?.code || 'HD001',
                        date: orderDataRef.current?.created || lastCreatedOrder?.created || new Date().toLocaleString('vi-VN'),
                        cashier: orderDataRef.current?.user_created_name || lastCreatedOrder?.user_created_name || currentUser?.username || 'Admin',
                        customer: orderDataRef.current?.customer_name || lastCreatedOrder?.customer_name || activeTab?.customer?.name || '',
                        items: orderDataRef.current?.items || lastCreatedOrder?.items || activeTab?.items || [],
                        subtotal: orderDataRef.current?.subtotal || lastCreatedOrder?.subtotal || totals.subtotal,
                        totalVat: orderDataRef.current?.vat || lastCreatedOrder?.vat || totals.totalVat,
                        discountAmount: orderDataRef.current?.discount || lastCreatedOrder?.discount || totals.discountAmount,
                        discountType: orderDataRef.current?.discount_type || lastCreatedOrder?.discount_type || discountType,
                        discount: orderDataRef.current?.discount_value || lastCreatedOrder?.discount_value || discount,
                        total: orderDataRef.current?.total || lastCreatedOrder?.total || totals.total,
                        paid: orderDataRef.current?.paid || lastCreatedOrder?.paid || paid,
                        change: orderDataRef.current?.change || lastCreatedOrder?.change || totals.change
                    }}
                />
            </div>

            {/* Add Customer Modal */}
            <AddCustomerModal
                open={showAddCustomerModal}
                onClose={handleCloseAddCustomer}
                onSuccess={handleAddCustomerSuccess}
            />
        </div>
    );
};

export default Pos;
